import React, { useEffect, useState } from "react";
import "./Dashboard.scss";
import { GetDashboard } from "./DashboardUtils";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Bar, Line } from "react-chartjs-2";
import { Chart, registerables, Filler } from "chart.js";
import IndexService from "../../Services/http/IndexService";
import Box from '@mui/material/Box';
import { Button } from "@mui/material";
import { changeDefaultAccount } from "../Account/AccountUtil";
import userRoles from "../../configs/userRolesConfigs";
import { useTranslation } from "react-i18next";
import { permissions } from "../../configs/permissions";

Chart.register(...registerables, Filler);

const Dashboard = () => {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isValidCreditCard, setIsValidCreditCard] = useState(true);
  const accounts = useSelector((state: any) => state.account.account_list);
  const userRole = useSelector((state: any) => state.general.userRol);
  const { accountId } = useParams();
  const casesDetails = useSelector((state: any) => state.dashboard.case_details);
  const mostReturnedProducts = useSelector((state: any) => state.dashboard.most_returned_products);
  const monthlyEarnings = useSelector((state: any) => state.dashboard.monthly_earnings);
  const missingDimensions = useSelector((state: any) => state.dashboard.missing_dimensions);
  const missingDocuments = useSelector((state: any) => state.dashboard.missing_documents);
  let defaultAccount = useSelector((state: any) => state.account.default_account);
  const allPermissions = useSelector((s: any) => s.general.user || {})?.permissions || [];
  const isPartner = useSelector((state: any) => state.partner.isPartnerLoggedIn);

  const can = (p: string) => {
    if (!isPartner) return true;
    return allPermissions.includes(p);
  }

  const canViewFeeAuditor = can(permissions.VIEW_FEE_AUDITOR);
  const canViewShipments = can(permissions.VIEW_SHIPMENTS);

  const colorByStatus = {
    "new": "#EB5757",
    "opened,reimbursed": "#0037AB",
    "reimbursed": "#27AE60"
  };

  const textByStatus = {
    "new": t("statuses.units"),
    "opened,reimbursed": t("statuses.cases"),
    "reimbursed": t("statuses.reimbursed")
  };

  const mapListFromJsonList = (jsonList: [], valueToMap: string) => {
    return jsonList.map((item) => item[valueToMap]);
  };

  const stateMostReturnedProducts = {
    labels: mapListFromJsonList(mostReturnedProducts, "fnsku"),
    datasets: [
      {
        label: "",
        backgroundColor: "#7AA1F3",
        borderColor: "rgba(0,0,0,1)",
        borderWidth: 0,
        borderRadius: 12,
        data: mapListFromJsonList(mostReturnedProducts, "amount"),
        barPercentage: 0.5,
      }
    ]
  };

  const options = {
    responsive: true,
    tension: 0.3,
    plugins: {
      legend: {
        display: false
      }
    }
  };

  const stateMonthlyEarnings = {
    labels: mapListFromJsonList(monthlyEarnings, "month"),
    datasets: [
      {
        label: "",
        data: mapListFromJsonList(monthlyEarnings, "sum_earnings"),
        borderColor: "#0037AB",
        backgroundColor: "rgba(0, 52, 136, 0.12)",
        fill: "origin"
      },
    ],
  };

  useEffect(() => {
    getIsValidCreditCard();
    if (defaultAccount && defaultAccount.id && (!accountId || +accountId === defaultAccount.id)) {
      GetDashboard(dispatch, defaultAccount.id);
    }
  }, [defaultAccount]);

  useEffect(() => {
    if (accountId) {
      let newAccount = accounts.find((account) => account.id === +accountId);
      changeDefaultAccount(dispatch, newAccount);
      navigate('/layout/dashboard');
    }
  }, [accountId]);

  const navigateToPage = (page: string) => {
    if (userRole !== userRoles.cfo) {
      if (page !== 'new') {
        if (userRole !== userRoles.account) {
          navigate(`/layout/cases/general/?type=${page}`);
        }
      } else {
        if (userRole !== userRoles.account) {
          navigate(`/layout/cases/new?type=${page}`);
        }
      }
    }
  };

  const navigateToFeeAuditors = () => {
    navigate("/layout/feeAuditors");
  };

  const navigateToShipment = () => {
    navigate("/layout/shipments");
  };

  const getIsValidCreditCard = async () => {
    let response = await IndexService.getIsValidCreditCard();
    setIsValidCreditCard(JSON.parse(JSON.stringify(response)));
  };

  const casesDetailsByState = casesDetails.reduce((acc, item) => {
    return {
      ...acc,
      [item.state]: item
    }
  }, {});

  return (
    <Box>
      <h2>{t("dashboardTitle")}</h2>
      <Box className="cases-box">
        <Box className="case-item" onClick={() => navigateToPage('new')}>
          <Box className="case-item-two">
            <Box className="cases-sum-box" sx={{ color: colorByStatus['new'] }}>
              {casesDetailsByState['new']?.units.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              })}
            </Box>
            <Box className="cases-description-box">{textByStatus['new']}</Box>
          </Box>
        </Box>
        <Box className="case-item" onClick={() => navigateToPage('reimbursed')}>
          <Box className="case-item-two">
            <Box className="cases-sum-box" sx={{ color: colorByStatus['reimbursed'] }}>
              ${casesDetailsByState['reimbursed']?.sum.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </Box>
            <Box className="cases-description-box">{textByStatus['reimbursed']}</Box>
          </Box>
        </Box>
        <Box className="case-item" onClick={() => navigateToPage('opened,reimbursed')}>
          <Box className="case-item-two">
            <Box className="cases-sum-box" sx={{ color: '#0037AB' }}>
              {casesDetailsByState['reimbursed']?.count + casesDetailsByState['opened']?.count}
            </Box>
            <Box className="cases-description-box">{textByStatus['opened,reimbursed']}</Box>
          </Box>
        </Box>
      </Box>

      <Box className="main-container">
        {canViewFeeAuditor && (
          <Box className="mr-10p">
            <h4 className="title">{t("titles.missingDimensions")}</h4>
            <Box className="grid-item">
              {missingDimensions.map((item) => (
                <Box key={item["fnsku"]} className="row-box">
                  <Box className="img-box"><img src={item["img"]} height={50} width={50} alt="product" /></Box>
                  <Box className="desc-box">
                    <span>
                      {item["product_name"].substr(0, 50)}
                      {item["product_name"].length > 50 && "..."}
                      <br />
                      FNSKU: {item["fnsku"]}
                    </span>
                  </Box>
                  <Box className="btn-box">
                    {userRole !== userRoles.cfo && <Button
                      onClick={() => navigateToFeeAuditors()}
                      className="listing-btn"
                    >
                      {t("actions.goToListing")}
                    </Button>}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
        {canViewShipments && (
          <Box className="ml-10p">
            <h4 className="title">{t("titles.missingDocuments")}</h4>
            <Box className="grid-item">
              <Box className="document-item">
                <Box sx={{ flexGrow: 1 }}>
                  {missingDocuments.map((item) => (
                    <Box key={item.shipment_id} className="item-documents">
                      <p>{t("shipment.id")}: {item.shipment_id}</p>
                      <p>{t("shipment.sku")}: {item.sku}</p>
                      <p>{t("shipment.shipped")}: {item.quantity_shipped}</p>
                      <p>{t("shipment.received")}: {item.quantity_received}</p>
                    </Box>
                  ))}
                </Box>
                <Box className="btn-box">
                  {userRole !== userRoles.cfo && <Button
                    onClick={() => navigateToShipment()}
                    className="blue-btn"
                  >
                    {t("actions.goToShipment")}
                  </Button>}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <Box className="main-container" sx={{ marginTop: "20px" }}>
        <Box className="mr-10p">
          <h4 className="title">{t("titles.returns")}</h4>
          <Box className="graph">
            <Bar data={stateMostReturnedProducts} options={{
              plugins: { legend: { display: false } },
              scales: {
                yAxes: { grid: { color: "#E6E6E6", lineWidth: 0.8 } },
                xAxes: { grid: { display: false } }
              }
            }} />
          </Box>
        </Box>
        <Box className="ml-10p">
          <h4 className="title">{t("titles.reimbursements")}</h4>
          <Box className="graph">
            <Line data={stateMonthlyEarnings} options={options} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
